import React, { useContext, useEffect } from 'react';
import { PromiseState } from 'react-refetch';
import { useSelector } from 'react-redux';

import OverviewContainer from 'components/Overview/Container/Container';
import { Grid } from 'components/Overview/Container/shared.style';
import TerritoryTable from './TerritoryTable';

import getTerritoryGridURL from 'url-constructors/getTerritoryGridUrl';
import connect from 'helpers/apiConnect';
import salesTypes from 'maps/sales-types';
import { EntityContext } from 'context/entityContext';

import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';
import metrics from 'maps/metrics';
import { checkIsExternalUser } from 'redux/reducers/authentication';

function dataSources(): any {
  return {
    fetchTerritoryTwtdData: (args: any): any => ({
      territoryTwtdFetch: getTerritoryGridURL(args),
    }),
    fetchTerritoryRtdData: (args: any): any => ({
      territoryRtdFetch: getTerritoryGridURL(args),
    }),
  };
}

interface ITerritoryPerformanceProps {
  fetchTerritoryTwtdData: Function;
  fetchTerritoryRtdData: Function;
  territoryTwtdFetch: PromiseState<any>;
  territoryRtdFetch: PromiseState<any>;
}

/*
 * The territory performance card shows two columns with a table in each.
 * Both tables show top performing countries and one depends on the dropdown metric.
 *
 * Note: as the `sortBy` parameter differs per endpoint, the global sort metric
 * is converted to one supported by this data container.
 */
const TerritoryPerformance: React.FC<ITerritoryPerformanceProps> = (props) => {
  const {
    fetchTerritoryTwtdData,
    fetchTerritoryRtdData,
    territoryTwtdFetch = PromiseState.create(),
    territoryRtdFetch = PromiseState.create(),
  } = props;
  const [, isPending] = useCombinedRefetchState([
    territoryTwtdFetch,
    territoryRtdFetch,
  ]);
  const isExternalUser = useSelector(checkIsExternalUser);

  const salesType = isExternalUser
    ? salesTypes.streaming
    : salesTypes.equivalents;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    compareMode,
    countryIds,
    filters,
    viewSettings,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';
  const countryFilter = filters?.country ?? undefined;
  const regionFilter = filters?.region ?? undefined;

  useEffect(() => {
    const args = {
      entityIds,
      entityType,
      salesType: salesType.value,
      limit: 5,
      countryIds: compareMode === 'country' ? countryIds : countryFilter,
      region: regionFilter,
      customRegion: customRegionFilter,
    };

    fetchTerritoryTwtdData({
      ...args,
      sortMetric: timeframe,
    });

    fetchTerritoryRtdData({
      ...args,
      sortMetric: 'rtd',
    });
  }, [
    fetchTerritoryTwtdData,
    fetchTerritoryRtdData,
    salesType,
    compareMode,
    entityIds,
    entityType,
    countryIds,
    timeframe,
    regionFilter,
    countryFilter,
    customRegionFilter,
  ]);

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Territory"
      subtitle={salesType.label}
      linkTo={useUrlForTopic('audience/territory')}
    >
      <Grid>
        <TerritoryTable
          title={`Top countries ${metrics[timeframe].label.toLowerCase()}`}
          activeMetric={timeframe}
          headers={['p.', 'country', metrics[timeframe].label]}
          territoryData={territoryTwtdFetch.value}
          highlightedCountryId={countryFilter}
          isPending={isPending}
        />
        <TerritoryTable
          title={'Top countries release to date'}
          activeMetric="rtd"
          headers={['p.', 'country', 'release to date']}
          territoryData={territoryRtdFetch.value}
          highlightedCountryId={countryFilter}
          isPending={isPending}
        />
      </Grid>
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(TerritoryPerformance as any);
